import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { StatePageSEO } from '@/components/seo';
import { StateFAQSection } from '@/components/faq';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * StatePage - Displays a single state landing page with full SEO
 * Fetches data from GET /api/public/state-pages/{slug}
 */
const StatePage = () => {
  const { slug } = useParams();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchState = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/public/state-pages/${slug}`);
        setState(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching state page:', err);
        setError(err.response?.data?.detail || 'Failed to load state information');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchState();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="state-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading state information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="state-error">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="state-not-found">
        <div>State information not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="state-page">
      {/* SEO Component */}
      <StatePageSEO 
        state={state} 
        baseUrl={BACKEND_URL?.replace('/api', '')}
      />
      
      {/* Page Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <header className="text-center mb-12">
          <div className="inline-block bg-blue-100 text-blue-800 text-sm px-4 py-2 rounded-full mb-4" data-testid="state-code">
            {state.state_code}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="state-heading">
            {state.page_heading || `Start a Business in ${state.state_name}`}
          </h1>
          {state.intro_paragraph && (
            <p className="text-lg text-gray-600" data-testid="state-intro">
              {state.intro_paragraph}
            </p>
          )}
        </header>

        {/* Key Information Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Government Fee Card */}
          <div className="bg-white rounded-xl shadow-sm p-6" data-testid="state-fee-card">
            <h2 className="text-base font-semibold text-gray-700 mb-2">LLC Filing Fee</h2>
            <div className="text-3xl font-bold text-green-600" data-testid="state-llc-fee">
              ${state.govt_fee_llc?.toFixed(2) || 'N/A'}
            </div>
            <p className="text-gray-500 mt-2 text-sm">State government fee</p>
          </div>

          {/* Processing Time Card */}
          <div className="bg-white rounded-xl shadow-sm p-6" data-testid="state-processing-card">
            <h2 className="text-base font-semibold text-gray-700 mb-2">Processing Time</h2>
            <div className="text-3xl font-bold text-blue-600" data-testid="state-processing-time">
              {state.processing_time_days ? `${state.processing_time_days} days` : 'N/A'}
            </div>
            <p className="text-gray-500 mt-2 text-sm">Standard processing</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-blue-600 rounded-xl shadow-lg p-8 text-center text-white">
          <h2 className="text-xl md:text-2xl font-bold mb-4">
            Ready to Start Your Business in {state.state_name}?
          </h2>
          <p className="text-blue-100 mb-6">
            Get started today with our professional formation services.
          </p>
          <button 
            className="bg-white text-blue-600 font-semibold py-3 px-8 rounded-xl hover:bg-blue-50 transition-colors"
            data-testid="state-cta-button"
          >
            Start My {state.state_code} Business
          </button>
        </div>

        {/* FAQ Section */}
        <StateFAQSection 
          stateName={state.state_name}
          stateCode={state.state_code}
          limit={5}
        />

        {/* Additional Benefits */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Why Form in {state.state_name}?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 text-xl">✓</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Simple Process</h3>
              <p className="text-gray-600 text-sm">Streamlined filing with expert guidance</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-green-600 text-xl">⚡</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Fast Processing</h3>
              <p className="text-gray-600 text-sm">Get your business up and running quickly</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-purple-600 text-xl">🛡</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Full Compliance</h3>
              <p className="text-gray-600 text-sm">Stay compliant with ongoing support</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatePage;
